import {
	AdAccountDTO,
	FacebookAdsCampaignDTO,
	GoogleAdsCampaignDTO
} from 'api-models'
import {
	ChangeEvent,
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useEffect,
	useState
} from 'react'
import { useSearchParams } from 'react-router-dom'

export type HandleSubItemChangeProps = {
	event?: ChangeEvent<HTMLInputElement>
	key: string
	value: string | string[] | number
}

export type OptionSubContentProps = {
	handleSubItemChange: ({ event, key, value }: HandleSubItemChangeProps) => void
	mapState: Map<any, any>
}

export type FiltersType = {
	[key: string]: string | string[] | undefined
	type?: string[]
	entity?: string
	reason?: string
	expiration_date?: string
}

interface BlacklistPageFiltersContextType {
	filters: FiltersType
	mapState: Map<any, any>
	selectedAdAccount: AdAccountDTO | null
	setSelectedAdAccount: Dispatch<SetStateAction<AdAccountDTO | null>>
	selectedCampaign: GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null
	setSelectedCampaign: Dispatch<
		SetStateAction<GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null>
	>
	handleApplyFilters: (newFilters: FiltersType) => void
	handleApplyDetailsItemFilter: (newFilters: FiltersType) => void
	handleSubItemChange: ({ event, key, value }: HandleSubItemChangeProps) => void
	handleRemoveFilter: (filterKeyToRemove: string) => void
	handleOpenClickDetailsChange: (open: boolean) => void
}
export const BlacklistPageFiltersContext = createContext(
	{} as BlacklistPageFiltersContextType
)

const FILTER_KEYS_TYPE_ARRAY = ['type']

const BLACKLIST_REASON_PARAM = 'blacklist_reason'

const VALID_FILTER_KEYS = [
	'type',
	'entity',
	BLACKLIST_REASON_PARAM,
	'expiration_date',
	'adAccount',
	'campaign',
	'breadCampaign'
]

const NAVIGATION_KEYS = ['adAccount', 'campaign', 'breadCampaign']

interface BlacklistPageFiltersContextProviderProps {
	children: ReactNode
}
export function BlacklistPageFiltersContextProvider({
	children
}: BlacklistPageFiltersContextProviderProps) {
	const [searchParams, setSearchParams] = useSearchParams()

	const [filters, setFilters] = useState({} as FiltersType)
	const [mapState, setMapState] = useState(new Map())
	const [selectedAdAccount, setSelectedAdAccount] =
		useState<AdAccountDTO | null>(null)
	const [selectedCampaign, setSelectedCampaign] = useState<
		GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null
	>(null)

	useEffect(() => {
		const filters: FiltersType = {}
		const map = new Map()
		const nextParams = new URLSearchParams(searchParams)

		searchParams.forEach((value, key) => {
			if (NAVIGATION_KEYS.includes(key)) return
			if (VALID_FILTER_KEYS.includes(key)) {
				const formattedValue = FILTER_KEYS_TYPE_ARRAY.includes(key)
					? value.split(',')
					: value
				const filterKey = key === BLACKLIST_REASON_PARAM ? 'reason' : key
				Object.assign(filters, { [filterKey]: formattedValue })
				map.set(filterKey, formattedValue)
			}
			if (key === 'reason') {
				nextParams.delete('reason')
			}
		})

		setFilters(filters)
		setMapState(map)
		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true })
		}
	}, [searchParams])

	const handleApplyFilters = (newFilters: FiltersType) => {
		setFilters(newFilters)
		const currentParams = new URLSearchParams(searchParams)

		Object.keys(newFilters).forEach((key) => {
			const value = newFilters[key]
			const urlKey = key === 'reason' ? BLACKLIST_REASON_PARAM : key
			if (VALID_FILTER_KEYS.includes(urlKey)) {
				if (value) {
					currentParams.set(urlKey, Array.isArray(value) ? value.join(',') : String(value))
				} else {
					currentParams.delete(urlKey)
				}
			}
		})
		currentParams.delete('reason')
		setSearchParams(currentParams)
	}

	const handleApplyDetailsItemFilter = (newFilters: FiltersType) => {
		const currentParams = new URLSearchParams(searchParams)

		for (const key of currentParams.keys()) {
			if (VALID_FILTER_KEYS.includes(key)) {
				currentParams.delete(key)
			}
		}
		currentParams.delete('reason')

		const map = new Map()
		Object.keys(newFilters).forEach((key) => {
			const value = newFilters[key] as string
			const urlKey = key === 'reason' ? BLACKLIST_REASON_PARAM : key
			if (VALID_FILTER_KEYS.includes(urlKey) && value) {
				map.set(key, value)
				currentParams.set(urlKey, value)
			}
		})

		setSearchParams(currentParams)
		setMapState(map)
		setFilters(newFilters)
	}

	const handleSubItemChange = ({
		event,
		key,
		value
	}: HandleSubItemChangeProps) => {
		if (event) event.stopPropagation()
		if (typeof value === 'string' && value.length === 0) {
			handleRemoveFilter(key)
			return
		}
		setMapState((map) => new Map(map.set(key, value)))
	}

	const handleRemoveFilter = (filterKeyToRemove: string) => {
		const copy = mapState
		copy.delete(filterKeyToRemove)
		setMapState(new Map(copy))

		const filters = mapFilters(copy)
		setFilters(filters)

		const urlKey = filterKeyToRemove === 'reason' ? BLACKLIST_REASON_PARAM : filterKeyToRemove
		if (VALID_FILTER_KEYS.includes(urlKey)) {
			const currentParams = new URLSearchParams(searchParams)
			currentParams.delete(urlKey)
			currentParams.delete('reason')
			setSearchParams(currentParams)
		}
	}

	const handleOpenClickDetailsChange = (open: boolean) => {
		if (!open) {
			const filters = mapFilters(mapState)
			handleApplyFilters(filters)
		}
	}

	const mapFilters = (map: Map<any, any>) => {
		const filters = {}
		map.forEach((value, key) => Object.assign(filters, { [key]: value }))
		return filters as FiltersType
	}

	return (
		<BlacklistPageFiltersContext.Provider
			value={{
				filters,
				mapState,
				selectedAdAccount,
				setSelectedAdAccount,
				selectedCampaign,
				setSelectedCampaign,
				handleApplyFilters,
				handleRemoveFilter,
				handleApplyDetailsItemFilter,
				handleOpenClickDetailsChange,
				handleSubItemChange
			}}
		>
			{children}
		</BlacklistPageFiltersContext.Provider>
	)
}
